"use client";

import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  SimpleGrid,
  Group,
  Button,
  Anchor,
  Divider,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import Link from "next/link";

const topColors = [
  { color: "#CD853F", name: "Terracotta", why: "Warm earth tone that harmonizes beautifully with olive undertones." },
  { color: "#556B2F", name: "Olive Green", why: "Matches your natural undertone—always flattering." },
  { color: "#722F37", name: "Burgundy", why: "Rich depth that complements the warmth in olive skin." },
  { color: "#008080", name: "Teal", why: "Balanced blue-green that works with olive's complexity." },
  { color: "#8B4513", name: "Chocolate Brown", why: "Deep, warm neutral that never clashes." },
  { color: "#DAA520", name: "Warm Gold", why: "Enhances the golden hues in olive skin." },
  { color: "#800020", name: "Wine", why: "Sophisticated depth without being too cool." },
  { color: "#2F4F4F", name: "Dark Slate", why: "Moody neutral that flatters without washing out." },
  { color: "#CC5A47", name: "Rust", why: "Warm, earthy, and perfectly complementary." },
  { color: "#9932CC", name: "Dark Orchid", why: "Purple with enough warmth to work with olive." },
  { color: "#FAEBD7", name: "Antique White", why: "Warm off-white that doesn't clash with green undertones." },
  { color: "#5F9EA0", name: "Cadet Blue", why: "Muted blue-green that creates soft harmony." },
];

const warmOliveColors = [
  { color: "#CD853F", name: "Terracotta" },
  { color: "#CC5A47", name: "Rust" },
  { color: "#DAA520", name: "Warm Gold" },
  { color: "#556B2F", name: "Olive Green" },
  { color: "#8B4513", name: "Chocolate" },
];

const coolOliveColors = [
  { color: "#008080", name: "Teal" },
  { color: "#722F37", name: "Burgundy" },
  { color: "#2F4F4F", name: "Dark Slate" },
  { color: "#9932CC", name: "Dark Orchid" },
  { color: "#5F9EA0", name: "Cadet Blue" },
];

const neutralOliveColors = [
  { color: "#800020", name: "Wine" },
  { color: "#FAEBD7", name: "Antique White" },
  { color: "#708090", name: "Slate Gray" },
  { color: "#BC8F8F", name: "Rosy Brown" },
  { color: "#6B8E23", name: "Olive Drab" },
];

const colorsToAvoid = [
  { color: "#ADD8E6", name: "Baby Blue", why: "Too cool and icy—makes olive skin look sallow", instead: "Teal or cadet blue" },
  { color: "#FFB6C1", name: "Light Pink", why: "Cool pastel that clashes with green undertones", instead: "Dusty rose or coral" },
  { color: "#FF4500", name: "Bright Orange", why: "Too intense—emphasizes yellow in skin unflattering", instead: "Terracotta or rust" },
  { color: "#FFFFFF", name: "Pure White", why: "Can look stark against olive skin", instead: "Antique white or cream" },
  { color: "#9ACD32", name: "Yellow-Green", why: "Too close to skin tone—looks muddy", instead: "Deep olive or forest green" },
];

function ColorSwatch({ color, size = 48 }: { color: string; size?: number }) {
  return (
    <Box
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: 8,
        border: color === "#FFFFFF" || color === "#FAEBD7" ? "1px solid #e0e0e0" : "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    />
  );
}

export default function BestColorsForOliveSkinContent() {
  return (
    <Container size="md" py="xl">
      {/* HERO */}
      <Stack gap="xs" mb={48}>
        <Title order={1} size={36} lh={1.2}>
          Best Colors for Olive Skin
        </Title>
        <Text size="xl" c="dimmed" maw={600}>
          Colors that work with your unique green undertone—not against it.
        </Text>
      </Stack>

      {/* SECTION 1: TOP 12 COLORS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Top 12 Best Colors for Olive Skin
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
          {topColors.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={56} />
              <Box style={{ flex: 1 }}>
                <Text fw={600} size="lg">{item.name}</Text>
                <Text c="dimmed" size="sm">{item.why}</Text>
              </Box>
            </Group>
          ))}
        </SimpleGrid>

        <Paper withBorder p="lg" mt={40} radius="md" bg="gray.0">
          <Text size="md" mb="sm">
            Not sure which of these is <em>your</em> best color?
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            rightSection={<IconArrowRight size={16} />}
          >
            Get your exact palette with AI Color Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 2: VISUAL PROOF */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          What These Colors Look Like on Olive Skin
        </Title>

        <Text c="dimmed" mb={32} maw={700}>
          Olive skin has a distinctive green undertone. The right colors harmonize with this—the wrong ones clash or make you look washed out.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} mb={32}>
          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="green" size="sm" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text fw={600}>Flattering</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#CD853F" size={40} />
              <ColorSwatch color="#556B2F" size={40} />
              <ColorSwatch color="#722F37" size={40} />
              <ColorSwatch color="#008080" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Earth tones and muted jewel colors create harmony with olive undertones.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="red" size="sm" radius="xl">
                <IconX size={14} />
              </ThemeIcon>
              <Text fw={600}>Unflattering</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#ADD8E6" size={40} />
              <ColorSwatch color="#FFB6C1" size={40} />
              <ColorSwatch color="#FF4500" size={40} />
              <ColorSwatch color="#9ACD32" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Cool pastels, bright orange, and yellow-greens clash with olive skin.
            </Text>
          </Paper>
        </SimpleGrid>

        <Paper p="lg" radius="md" bg="blue.0">
          <Text fw={600} mb="xs">Why olive skin is unique:</Text>
          <Text size="sm">
            Olive undertones have a green or yellow-green cast that's technically neutral but leans warm. This means cool pastels often look wrong, but so do colors too close to your skin's natural hue. The sweet spot? Warm earth tones and muted jewel colors.
          </Text>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 3: BY UNDERTONE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Best Colors for Olive Skin by Type
        </Title>

        <Stack gap={40}>
          {/* Warm Olive */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Warm Olive
            </Title>
            <Text size="sm" c="dimmed" mb={16}>More golden-green undertones. Gold jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {warmOliveColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> These colors enhance the golden warmth in your skin, creating a rich, harmonious look.
            </Text>
          </Box>

          {/* Cool Olive */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Cool Olive
            </Title>
            <Text size="sm" c="dimmed" mb={16}>More gray-green undertones. Silver jewelry can work.</Text>

            <Group gap="lg" mb={16}>
              {coolOliveColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> These colors have depth without being too icy, balancing the cooler elements of your olive undertone.
            </Text>
          </Box>

          {/* Neutral Olive */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Neutral Olive
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Balanced green undertones. Both gold and silver work.</Text>

            <Group gap="lg" mb={16}>
              {neutralOliveColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Muted, balanced colors that aren't too warm or too cool—they let your natural coloring shine.
            </Text>
          </Box>
        </Stack>

        <Paper withBorder p="lg" mt={40} radius="md" bg="teal.0">
          <Text fw={600} mb="sm">
            Most people with olive skin struggle to find their undertone.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            size="md"
            rightSection={<IconArrowRight size={16} />}
          >
            Find your exact undertone + best colors instantly
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 4: COLORS TO AVOID */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Colors That Make Olive Skin Look Sallow
        </Title>

        <Text c="dimmed" mb={32}>
          Some colors emphasize the green in olive skin in an unflattering way.
        </Text>

        <Stack gap={20}>
          {colorsToAvoid.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={48} />
              <Box style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text fw={600}>{item.name}</Text>
                  <Text c="red.6" size="sm">— {item.why}</Text>
                </Group>
                <Text size="sm" c="teal.7">
                  Instead, choose: <strong>{item.instead}</strong>
                </Text>
              </Box>
            </Group>
          ))}
        </Stack>
      </Box>

      <Divider my={48} />

      {/* SECTION 5: BLACK */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Does Black Work on Olive Skin?
        </Title>

        <Text c="dimmed" mb={24}>
          Black is tricky for olive skin—it can work, but there are often better options.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={16} mb={24}>
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="xs">When Black Works</Text>
            <Text size="sm" c="dimmed">
              If you have dark hair and high contrast, black can look great. Pair it with warm accessories (gold jewelry, warm-toned makeup).
            </Text>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="xs">Better Alternatives</Text>
            <Text size="sm" c="dimmed">
              Charcoal, dark chocolate brown, navy, or dark olive. These have depth without the harsh contrast of pure black.
            </Text>
          </Paper>
        </SimpleGrid>
      </Box>

      <Divider my={48} />

      {/* SECTION 6: SEASONAL */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Your Exact Color Season Matters
        </Title>

        <Text c="dimmed" mb={8}>
          The truth about olive skin:
        </Text>

        <Paper p="md" radius="md" bg="gray.1" mb={24}>
          <Text fw={600} size="lg" ta="center">
            Olive skin doesn't fit neatly into standard color analysis.
          </Text>
        </Paper>

        <Text mb={32}>
          Most people with olive skin fall into Autumn seasons—but some are Summers. Your exact <strong>color season</strong> determines your perfect palette.
        </Text>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16} mb={32}>
          <Anchor component={Link} href="/color-palette/autumn/soft" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#A0522D" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#6B8E23" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#D2B48C" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Soft Autumn</Text>
              <Text size="xs" c="dimmed">Muted & warm</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/autumn/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#CD853F" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#228B22" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#8B4513" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">True Autumn</Text>
              <Text size="xs" c="dimmed">Rich & earthy</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/autumn/deep" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#800020" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#556B2F" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#8B4513" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Deep Autumn</Text>
              <Text size="xs" c="dimmed">Deep & warm</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/summer/cool" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#708090" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#BC8F8F" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#8FBC8F" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Soft Summer</Text>
              <Text size="xs" c="dimmed">Cool olive option</Text>
            </Paper>
          </Anchor>
        </SimpleGrid>

        <Paper p="xl" radius="md" bg="teal.6" c="white" ta="center">
          <Text size="lg" fw={600} mb="sm">
            Get your personalized color palette
          </Text>
          <Text size="sm" mb="md" opacity={0.9}>
            Takes 30 seconds. See your exact best colors.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            variant="white"
            size="lg"
            rightSection={<IconArrowRight size={18} />}
          >
            Start Free Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 7: FAQ */}
      <Box mb={48}>
        <Title order={2} size={28} mb={32}>
          Frequently Asked Questions
        </Title>

        <Stack gap={24}>
          <Box>
            <Text fw={600} mb={4}>What colors look best on olive skin?</Text>
            <Text c="dimmed">
              Earth tones like terracotta, rust, and olive green. Also burgundy, teal, warm gold, and chocolate brown. These colors harmonize with olive's unique green undertone.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Is olive skin warm or cool toned?</Text>
            <Text c="dimmed">
              Olive skin is technically neutral with a green undertone, but most olive skin leans warm. Some people have cool olive skin, which is less common.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>What colors should olive skin avoid?</Text>
            <Text c="dimmed">
              Cool pastels like baby blue and light pink, bright orange, pure white, and yellow-green colors too close to your skin tone. These can make olive skin look sallow.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Does black look good on olive skin?</Text>
            <Text c="dimmed">
              It can, especially with high contrast coloring. But charcoal, dark brown, or navy are often more flattering alternatives.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>What hair color suits olive skin?</Text>
            <Text c="dimmed">
              Warm brunettes, chestnut, auburn, and copper. Avoid ashy or cool-toned colors that can clash with olive undertones.
            </Text>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}
